import React, { useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import fnc from '../../mymod/commFunction';
import './SmsSend.scss';

const TAB = {
    SMS: 'sms',
    MMS: 'mms'
};

const SMS_LIMIT_BYTES = 90;
const MESSAGE_PREFIX = '[매쓰테일러] ';
const MMS_MAX_FILES = 5;

function formatDateTimeForApi(dateValue, timeValue) {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';

    const [timeHh = '00', timeMm = '00'] = String(timeValue || '').split(':');
    d.setHours(parseInt(timeHh, 10) || 0);
    d.setMinutes(parseInt(timeMm, 10) || 0);
    d.setSeconds(0);

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${day} ${hh}:${mm}:${ss}`;
}

function nowForTimeInput() {
    const now = new Date();
    const hh = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
}

function normalizePhone(value) {
    const raw = String(value || '').trim();
    if (!raw || raw.toUpperCase() === 'NULL') return '';
    return raw.replace(/[^0-9]/g, '');
}

function resolveUserPhone(user) {
    return (
        normalizePhone(user?.hp)
        || normalizePhone(user?.mobile)
        || normalizePhone(user?.phone)
        || ''
    );
}

function prependMessagePrefix(message) {
    const text = String(message || '');
    if (!text) return '';
    if (text.startsWith(MESSAGE_PREFIX) || text.startsWith('[매쓰테일러]')) return text;
    return `${MESSAGE_PREFIX}${text}`;
}

function joinUrl(base, path) {
    const b = String(base || '').replace(/\/$/, '');
    const p = String(path || '').replace(/^\//, '');
    return b ? `${b}/${p}` : `/${p}`;
}

function sanitizeFileName(name) {
    return String(name || 'file')
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/_+/g, '_');
}

function toS3PublicUrl(s3Key) {
    const encodedKey = String(s3Key || '')
        .split('/')
        .map((part) => encodeURIComponent(part))
        .join('/');
    return `http://mathautogen.s3.ap-northeast-2.amazonaws.com/${encodedKey}`;
}

function loadImageFromFile(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('이미지 로딩 실패'));
            img.src = reader.result;
        };
        reader.onerror = () => reject(new Error('파일 읽기 실패'));
        reader.readAsDataURL(file);
    });
}

async function convertToJpegFile(file) {
    const image = await loadImageFromFile(file);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('이미지 변환 컨텍스트 생성 실패');
    }

    // PNG 투명 영역이 검정으로 변하는 것을 방지하기 위해 흰 배경을 깐다.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);

    const jpegBlob = await new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    });

    if (!jpegBlob) {
        throw new Error('JPG 변환 실패');
    }

    const rawName = String(file?.name || 'mms_image').replace(/\.[^/.]+$/, '');
    const safeBaseName = sanitizeFileName(rawName);
    return new File([jpegBlob], `${safeBaseName}.jpg`, { type: 'image/jpeg' });
}

const SmsSend = () => {
    const [tab, setTab] = useState(TAB.SMS);
    const [sending, setSending] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState(new Set());
    const [keyword, setKeyword] = useState('');
    const [sortKey, setSortKey] = useState('id');
    const [sortDir, setSortDir] = useState('asc');
    const [mmsFiles, setMmsFiles] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);

    const [form, setForm] = useState({
        mode: 1,
        sendDate: new Date(),
        sendTime: nowForTimeInput(),
        toPhone: '',
        personName: '',
        studentNo: '',
        message: '',
        subject: '',
        fileCount: 0,
        filePath1: '',
        filePath2: '',
        filePath3: '',
        filePath4: '',
        filePath5: ''
    });

    const tabLabel = useMemo(() => (tab === TAB.SMS ? 'SMS' : 'MMS'), [tab]);
    const smsMessageBytes = useMemo(() => new TextEncoder().encode(form.message || '').length, [form.message]);
    const isSmsOverLimit = useMemo(
        () => tab === TAB.SMS && smsMessageBytes > SMS_LIMIT_BYTES,
        [tab, smsMessageBytes]
    );

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        const onWindowError = (event) => {
            const msg = event?.message || '';
            if (msg.includes('ResizeObserver loop completed with undelivered notifications')) {
                event.stopImmediatePropagation();
            }
        };

        const originalConsoleError = console.error;
        console.error = (...args) => {
            const joined = args.map((arg) => String(arg)).join(' ');
            if (joined.includes('ResizeObserver loop completed with undelivered notifications')) {
                return;
            }
            originalConsoleError(...args);
        };

        window.addEventListener('error', onWindowError);
        return () => {
            window.removeEventListener('error', onWindowError);
            console.error = originalConsoleError;
        };
    }, []);

    const filteredUsers = useMemo(() => {
        const key = (keyword || '').trim().toLowerCase();
        if (!key) return users;

        return users.filter((user) => {
            const id = String(user.id || '').toLowerCase();
            const name = String(user.stName || '').toLowerCase();
            const stCode = String(user.stCode || '').toLowerCase();
            const hp = String(user.hp || '').toLowerCase();
            const academyName = String(user.academyName || '').toLowerCase();
            return id.includes(key) || name.includes(key) || stCode.includes(key) || hp.includes(key) || academyName.includes(key);
        });
    }, [users, keyword]);

    const sortedFilteredUsers = useMemo(() => {
        const copied = [...filteredUsers];
        copied.sort((a, b) => {
            const aValue = String(a[sortKey] || '').toLowerCase();
            const bValue = String(b[sortKey] || '').toLowerCase();
            if (aValue < bValue) return sortDir === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return copied;
    }, [filteredUsers, sortKey, sortDir]);

    const selectedCount = useMemo(() => selectedKeys.size, [selectedKeys]);

    function getRowKey(user) {
        return `${user.id}__${user.stCode}__${user.hp}`;
    }

    function onChange(key, value) {
        setForm((prev) => ({ ...prev, [key]: value }));
    }

    function sendAxiosAsync(url, data) {
        return fetch(joinUrl(fnc.svrUrl, url), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data || {})
        })
            .then(async (response) => {
                let json = {};
                try {
                    json = await response.json();
                } catch (e) {
                    json = {};
                }

                if (!response.ok) {
                    return {
                        success: false,
                        message: json?.message || `HTTP ${response.status}`,
                        detail: json?.detail || ''
                    };
                }

                return json || {};
            })
            .catch((error) => ({ success: false, message: error.message || '요청 실패' }));
    }

    function fetchUsers() {
        setLoadingUsers(true);
        fnc.sendAxios('/userRoutes/selectUsers', {}, (res) => {
            const mapped = Array.isArray(res)
                ? res.map((item) => ({
                    id: item.id || '',
                    stName: item.stName || '',
                    stCode: item.stCode || '',
                    hp: item.hp || '',
                    mobile: item.mobile || '',
                    phone: item.phone || '',
                    sDate: item.sDate || '',
                    academyName: item.academyName || ''
                }))
                : [];

            setUsers(mapped);
            setLoadingUsers(false);
        });
    }

    function selectSingleUser(user) {
        setSelectedKeys(new Set([getRowKey(user)]));
    }

    function toggleUser(user) {
        const key = getRowKey(user);
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    function toggleAllUsers() {
        if (sortedFilteredUsers.every((u) => selectedKeys.has(getRowKey(u)))) {
            setSelectedKeys(new Set());
        } else {
            setSelectedKeys(new Set(sortedFilteredUsers.map(getRowKey)));
        }
    }

    function handleSort(key) {
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortKey(key);
        setSortDir('asc');
    }

    function getSortMark(key) {
        if (sortKey !== key) return '↕';
        return sortDir === 'asc' ? '↑' : '↓';
    }

    function buildPayload() {
        const payload = {
            mode: Number(form.mode),
            sendDate: formatDateTimeForApi(form.sendDate, form.sendTime),
            personName: form.personName,
            studentNo: form.studentNo,
            message: prependMessagePrefix(form.message)
        };

        if (tab === TAB.SMS) {
            return payload;
        }

        payload.subject = form.subject;
        payload.fileCount = 0;
        payload.filePath1 = '';
        payload.filePath2 = '';
        payload.filePath3 = '';
        payload.filePath4 = '';
        payload.filePath5 = '';
        return payload;
    }

    function handleMmsFileChange(event) {
        const list = Array.from(event.target.files || []);
        const imageFiles = list.filter((file) => String(file.type || '').startsWith('image/'));

        if (list.length !== imageFiles.length) {
            alert('이미지 파일만 첨부할 수 있습니다.');
        }

        if (imageFiles.length > MMS_MAX_FILES) {
            alert(`이미지는 최대 ${MMS_MAX_FILES}개까지 첨부할 수 있습니다.`);
        }

        setMmsFiles(imageFiles.slice(0, MMS_MAX_FILES));
    }

    async function uploadMmsImagesToS3(files) {
        const uploadedUrls = [];
        const uploadApiUrl = joinUrl(fnc.svrUrl, '/s3Routes/uploadS3');

        for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            // MMS 파일 포맷 오류를 피하기 위해 항상 JPG로 변환하여 업로드한다.
            // eslint-disable-next-line no-await-in-loop
            const jpegFile = await convertToJpegFile(file);
            const baseName = sanitizeFileName(String(jpegFile.name || `mms_${i}.jpg`).replace(/\.[^/.]+$/, ''));
            const s3Key = `public/sms/mms/${Date.now()}_${i + 1}_${baseName}.jpg`;

            const formData = new FormData();
            formData.append('file', jpegFile, `${baseName}.jpg`);
            formData.append('s3Key', s3Key);

            // eslint-disable-next-line no-await-in-loop
            const response = await fetch(uploadApiUrl, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || '이미지 업로드 실패');
            }

            // eslint-disable-next-line no-await-in-loop
            const json = await response.json();
            const key = json?.key || s3Key;

            // MMS 서버 호환을 위해 쿼리스트링 없는 고정 public URL을 전달한다.
            uploadedUrls.push(toS3PublicUrl(key));
        }

        return uploadedUrls;
    }

    async function handleSend() {
        if (!form.message) {
            alert('전송메세지는 필수입니다.');
            return;
        }

        if (Number(form.mode) === 0 && !form.sendDate) {
            alert('예약 모드에서는 전송일을 입력해야 합니다.');
            return;
        }

        if (selectedKeys.size === 0) {
            alert('전송할 회원을 선택해주세요.');
            return;
        }

        const selectedUsers = users.filter((item) => selectedKeys.has(getRowKey(item)));
        const endpoint = tab === TAB.MMS ? '/smsRoutes/sendMms' : '/smsRoutes/sendSms';
        const previewUser = selectedUsers[0] || {};
        const previewPhone = resolveUserPhone(previewUser) || '번호없음';
        const sendModeText = Number(form.mode) === 0 ? `예약 (${formatDateTimeForApi(form.sendDate, form.sendTime)})` : '즉시';

        const ok = window.confirm(
            [
                `${tabLabel} 전송 하시겠습니까?`,
                `이름: ${previewUser.stName || '-'}`,
                `번호: ${previewPhone}`,
                `발송방식: ${sendModeText}`
            ].join('\n')
        );

        if (!ok) {
            return;
        }

        let successCount = 0;
        let failCount = 0;
        let noPhoneCount = 0;

        let uploadedImageUrls = [];
        if (tab === TAB.MMS && mmsFiles.length > 0) {
            setUploadingImages(true);
            try {
                uploadedImageUrls = await uploadMmsImagesToS3(mmsFiles);
            } catch (error) {
                setUploadingImages(false);
                alert(`MMS 이미지 업로드 실패: ${error.message}`);
                return;
            }
            setUploadingImages(false);
        }

        setSending(true);
        for (const user of selectedUsers) {
            const toPhone = resolveUserPhone(user);
            if (toPhone.length < 10) {
                noPhoneCount += 1;
                continue;
            }

            const payload = buildPayload();
            payload.toPhone = toPhone;
            payload.personName = user.stName || payload.personName;

            if (endpoint === '/smsRoutes/sendMms') {
                payload.subject = payload.subject || '알림';
                payload.fileCount = uploadedImageUrls.length;
                payload.filePath1 = uploadedImageUrls[0] || '';
                payload.filePath2 = uploadedImageUrls[1] || '';
                payload.filePath3 = uploadedImageUrls[2] || '';
                payload.filePath4 = uploadedImageUrls[3] || '';
                payload.filePath5 = uploadedImageUrls[4] || '';
            }

            // 순차 전송으로 서버 부하와 실패 시점 파악을 단순화한다.
            // eslint-disable-next-line no-await-in-loop
            const res = await sendAxiosAsync(endpoint, payload);
            if (res && res.success && Number(res.insertedCount || 0) > 0) {
                successCount += 1;
            } else {
                if (res?.message) {
                    console.error('MMS/SMS send error detail:', res.message, res.detail || '');
                }
                failCount += 1;
            }
        }

        setSending(false);
        const modeText = endpoint === '/smsRoutes/sendMms' ? 'MMS' : 'SMS';
        alert(
            `${modeText} 전송 등록 완료\n성공: ${successCount}건\n실패: ${failCount}건\n전화번호 없음: ${noPhoneCount}건`
        );
    }

    return (
        <div className="SmsSend">
            <div className="sms-tab-buttons">
                <button
                    type="button"
                    className={tab === TAB.SMS ? 'active' : ''}
                    onClick={() => setTab(TAB.SMS)}
                >
                    SMS
                </button>
                <button
                    type="button"
                    className={tab === TAB.MMS ? 'active' : ''}
                    onClick={() => setTab(TAB.MMS)}
                >
                    MMS
                </button>
            </div>

            <div className={`sms-form-box ${tab === TAB.SMS ? 'is-sms' : ''}`}>
                <div className="sms-layout">

                    {/* 테이블 패널 (왼쪽) */}
                    <div className="sms-right-panel">
                        <div className="member-toolbar">
                            <input
                                className="member-search"
                                placeholder="회원 검색"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                            />
                            <button type="button" onClick={fetchUsers}>회원 새로고침</button>
                        </div>

                        <div className="member-summary">
                            <span>전체 {users.length}명</span>
                            <span>검색결과 {sortedFilteredUsers.length}명</span>
                            <span>선택 {selectedCount}명</span>
                        </div>

                        <div className="member-table-wrap">
                            <table className="member-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                checked={sortedFilteredUsers.length > 0 && sortedFilteredUsers.every((u) => selectedKeys.has(getRowKey(u)))}
                                                onChange={toggleAllUsers}
                                                title="전체 선택"
                                            />
                                        </th>
                                        <th>
                                            <button type="button" className="sort-btn" onClick={() => handleSort('stName')}>
                                                이름 {getSortMark('stName')}
                                            </button>
                                        </th>
                                        <th>
                                            <button type="button" className="sort-btn" onClick={() => handleSort('id')}>
                                                아이디 {getSortMark('id')}
                                            </button>
                                        </th>
                                        <th>
                                            <button type="button" className="sort-btn" onClick={() => handleSort('stCode')}>
                                                stCode {getSortMark('stCode')}
                                            </button>
                                        </th>
                                        <th>
                                            <button type="button" className="sort-btn" onClick={() => handleSort('hp')}>
                                                휴대폰 {getSortMark('hp')}
                                            </button>
                                        </th>
                                        <th>
                                            <button type="button" className="sort-btn" onClick={() => handleSort('sDate')}>
                                                시작일(가입일) {getSortMark('sDate')}
                                            </button>
                                        </th>
                                        <th>
                                            <button type="button" className="sort-btn" onClick={() => handleSort('academyName')}>
                                                학원명 {getSortMark('academyName')}
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loadingUsers ? (
                                        <tr>
                                            <td colSpan="7" className="empty">회원 목록 조회 중...</td>
                                        </tr>
                                    ) : sortedFilteredUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="empty">회원이 없습니다.</td>
                                        </tr>
                                    ) : (
                                        sortedFilteredUsers.map((user) => (
                                            <tr
                                                key={`${user.id}-${user.stCode}`}
                                                className={selectedKeys.has(getRowKey(user)) ? 'is-selected' : ''}
                                                onClick={() => selectSingleUser(user)}
                                            >
                                                <td onClick={(e) => { e.stopPropagation(); toggleUser(user); }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedKeys.has(getRowKey(user))}
                                                        onChange={() => {}}
                                                    />
                                                </td>
                                                <td>{user.stName}</td>
                                                <td>{user.id}</td>
                                                <td>{user.stCode}</td>
                                                <td>{user.hp}</td>
                                                <td>{String(user.sDate || '').slice(0, 10)}</td>
                                                <td>{user.academyName}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {tab === TAB.SMS && (
                            <div className="sms-inline-message-wrap">
                                <select
                                    className="sms-inline-mode"
                                    value={form.mode}
                                    onChange={(e) => onChange('mode', e.target.value)}
                                >
                                    <option value={1}>즉시</option>
                                    <option value={0}>예약</option>
                                </select>
                                <DatePicker
                                    selected={form.sendDate}
                                    onChange={(date) => onChange('sendDate', date)}
                                    locale={ko}
                                    dateFormat="yyyy-MM-dd"
                                    className="sms-inline-date"
                                    calendarClassName="sms-datepicker-calendar"
                                    popperClassName="sms-datepicker-popper"
                                />
                                <input
                                    className="sms-inline-time"
                                    type="time"
                                    value={form.sendTime}
                                    onChange={(e) => onChange('sendTime', e.target.value)}
                                />
                                <input
                                    className={`sms-inline-message ${isSmsOverLimit ? 'is-over-limit' : ''}`}
                                    value={form.message}
                                    onChange={(e) => onChange('message', e.target.value)}
                                    placeholder="전송메세지"
                                />
                                <button
                                    type="button"
                                    className="inline-send-btn"
                                    onClick={handleSend}
                                    disabled={sending || loadingUsers}
                                >
                                    {sending ? '전송 등록 중...' : `전송`}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* SMS 선택된 사용자 패널 (오른쪽) */}
                    {tab === TAB.SMS && (
                        <div className="sms-selected-panel">
                            {selectedKeys.size > 0 ? (
                                <div className="selected-users-preview">
                                    {users.filter((u) => selectedKeys.has(getRowKey(u))).map((u) => (
                                        <span key={getRowKey(u)} className="selected-user-tag">
                                            {u.stName} {u.academyName}
                                            <button type="button" className="tag-remove" onClick={() => toggleUser(u)}>×</button>
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <p className="selected-empty">선택된 회원이 없습니다.</p>
                            )}
                        </div>
                    )}

                    {/* MMS 입력 패널 (오른쪽) */}
                    {tab === TAB.MMS && (
                        <div className="sms-left-panel">
                            {selectedKeys.size > 0 && (
                                <div className="selected-users-preview">
                                    {users.filter((u) => selectedKeys.has(getRowKey(u))).map((u) => (
                                        <span key={getRowKey(u)} className="selected-user-tag">
                                            {u.stName} {u.academyName}
                                            <button type="button" className="tag-remove" onClick={() => toggleUser(u)}>×</button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            <div className="mms-datetime-row">
                                <select className="sms-inline-mode" value={form.mode} onChange={(e) => onChange('mode', e.target.value)}>
                                    <option value={1}>즉시</option>
                                    <option value={0}>예약</option>
                                </select>
                                <DatePicker
                                    selected={form.sendDate}
                                    onChange={(date) => onChange('sendDate', date)}
                                    locale={ko}
                                    dateFormat="yyyy-MM-dd"
                                    className="sms-inline-date"
                                    calendarClassName="sms-datepicker-calendar"
                                    popperClassName="sms-datepicker-popper"
                                />
                                <input
                                    className="sms-inline-time"
                                    type="time"
                                    value={form.sendTime}
                                    onChange={(e) => onChange('sendTime', e.target.value)}
                                />
                            </div>

                            <label className="label-inline mms-subject-row">
                                <span>제목</span>
                                <input value={form.subject} onChange={(e) => onChange('subject', e.target.value)} />
                            </label>

                            <div className="form-grid">
                                <label>
                                    <span>이미지 첨부 (최대 5개)</span>
                                    <input type="file" accept="image/*" multiple onChange={handleMmsFileChange} />
                                </label>
                            </div>

                            {mmsFiles.length > 0 && (
                                <div className="mms-file-list">
                                    {mmsFiles.map((file) => (
                                        <span key={file.name + file.size}>{file.name}</span>
                                    ))}
                                </div>
                            )}

                            <label className="message-box no-label-field">
                                <textarea
                                    rows={8}
                                    value={form.message}
                                    onChange={(e) => onChange('message', e.target.value)}
                                    placeholder="전송메세지"
                                />
                            </label>

                            <div className="form-actions">
                                <button type="button" onClick={handleSend} disabled={sending || loadingUsers || uploadingImages}>
                                    {uploadingImages ? '이미지 업로드 중...' : sending ? '전송 등록 중...' : `${tabLabel} 전송`}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SmsSend;
